<template>
    <div class="search-order-component">
        <!-- Поле поиска -->
        <div class="search-container">
            <div class="search-input-wrapper">
                <input
                    v-model="searchQuery"
                    @keyup.enter="performSearch"
                    placeholder="🔍 Введите номер заказа..."
                    class="search-input"
                    :disabled="loading"
                />
                <button 
                    @click="performSearch"
                    class="search-button"
                    :disabled="loading || !searchQuery.trim()"
                >
                    {{ loading ? 'Поиск...' : 'Найти' }}
                </button>
                <button 
                    v-if="searchQuery.trim() || searchResult"
                    @click="clearSearch"
                    class="clear-button"
                >
                    ✕
                </button>
            </div>
            
            <!-- Сообщения о состоянии поиска -->
            <div v-if="loading" class="search-status">
                <span class="search-loading">Поиск заказа...</span>
            </div>
            
            <div v-if="error" class="search-status">
                <span class="search-error">❌ {{ error }}</span>
            </div>
            
            <div v-if="noResults && !loading" class="search-status">
                <span class="search-no-results">Заказ с номером "{{ searchQuery }}" не найден</span>
            </div>
        </div>

        <!-- Результат поиска -->
        <div v-if="searchResult" class="search-result">
            <div class="result-header">
                <h3>Найденный заказ</h3>
                <span class="result-found">Найден по запросу: "{{ searchQuery }}"</span>
            </div>
            
            <!-- Карточка найденного заказа -->
            <div class="drop-order-block found-order-block">
                <div
                    class="size-change-floating"
                    v-if="canChangeSize(searchResult)"
                    @click="handleSizeChange($event, searchResult)"
                    :disabled="loadingActions[searchResult.order_id] === 'change_size'"
                    title="Изменить размер"
                >
                    {{ loadingActions[searchResult.order_id] === 'change_size' ? '...' : '📏' }}
                </div>

                <img 
                    :src="searchResult.product_photo_url || require('@/assets/images-templates/пример-фото-товара.jpg')" 
                    :alt="searchResult.product_name" 
                    class="drop-order-product-img"
                />

                <!-- Блок с отображением данных -->
                <div class="drop-orders-data">
                    <!-- Если админский режим - показываем информацию о дроп-пользователе -->
                    <div v-if="searchResult.order_type === 'drop'" class="drop-orders-data-line drop-info-line">
                        <div class="drop-order-data-label">Дроп:</div>
                        <div class="drop-order-data-value">
                            {{ searchResult.drop_user_name || 'Неизвестный дроп' }}
                            <span v-if="searchResult.drop_username" class="drop-username"> ({{ searchResult.drop_username }})</span>
                        </div>
                    </div>

                    <!-- Линия данных (название)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Название:</div>
                        <div class="drop-order-data-value">{{ searchResult.product_name }}</div>
                    </div>

                    <!-- Линия данных (размер и количество)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Размер и кол-во:</div>
                        <div class="drop-order-data-value">{{ searchResult.size }} x {{ searchResult.quantity }}</div>
                    </div>

                    <!-- Линия данных (номер заказа)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Номер заказа:</div>
                        <div class="drop-order-data-value order-number-highlight">{{ searchResult.order_id }}</div>
                    </div>

                    <!-- Линия данных (трек-номер)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Трек-номер:</div>
                        <div class="drop-order-data-value">{{ searchResult.order_number }}</div>
                    </div>

                    <!-- Линия данных (Служба доставки)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Служба доставки:</div>
                        <div class="drop-order-data-value">{{ searchResult.delivery_service }}</div>
                    </div>

                    <!-- Линия данных (Оплачено ли)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Оплата внесена:</div>
                        <div class="drop-order-data-value">
                            <span :class="{ 'payment-paid': searchResult.is_payment, 'payment-not-paid': !searchResult.is_payment }">
                                {{ searchResult.is_payment ? 'Да' : 'Нет' }}
                            </span>
                        </div>
                    </div>

                    <!-- Линия данных (Статус заказа)-->
                    <div class="drop-orders-data-line">
                        <div class="drop-order-data-label">Статус:</div>
                        <div class="drop-order-data-value">{{ getStatusText(searchResult.status) }}</div>
                    </div>
                </div>

                <!-- Блок кнопок для администраторов -->
                <div class="buttons-block">
                    <!-- Заграничный конвейер: Заказан → Прибыл в Китай → Отправлено в РФ → Прибыл в РФ -->
                    <div class="accept-order"
                        v-for="step in abroadSteps.filter(st => st.from === searchResult.status)"
                        :key="step.action"
                        @click="handleAbroadStep($event, searchResult, step)"
                        :disabled="loadingActions[searchResult.order_id] === step.action"
                    >
                        {{ loadingActions[searchResult.order_id] === step.action ? 'Обработка...' : step.label }}
                    </div>

                    <!-- Кнопка возврата заказа (ТОЛЬКО ДЛЯ ЗАКАЗОВ С АВИТО) -->
                    <div class="return-order"
                        v-if="searchResult.status === 'in_transit' && searchResult.order_type === 'avito'"
                        @click="handleAdminReturn($event, searchResult)"
                        :disabled="loadingActions[searchResult.id] === 'return'"
                    >
                        {{ loadingActions[searchResult.id] === 'return' ? 'Обработка...' : 'Возврат' }}
                    </div>

                    <!-- Кнопка закрытия возврата (менеджер физически получил товар обратно) -->
                    <div class="accept-order"
                        v-if="searchResult.status === 'on_return'"
                        @click="handleCloseReturn($event, searchResult)"
                        :disabled="loadingActions[searchResult.id] === 'close_return'"
                    >
                        {{ loadingActions[searchResult.id] === 'close_return' ? 'Обработка...' : 'Закрыть возврат' }}
                    </div>

                    <!-- КНОВЫЙ: Кнопка подтверждения заказа (только для waiting_confirmation) -->
                    <div class="accept-order"
                        v-if="searchResult.status === 'waiting_confirmation'"
                        @click="handleConfirm($event, searchResult)"
                        :disabled="loadingActions[searchResult.id] === 'confirm'"
                    >
                        {{ loadingActions[searchResult.id] === 'confirm' ? 'Обработка...' : 'Принять заказ' }}
                    </div>
                    
                    <!-- Кнопка отправки заказа (только для waiting_shipment) -->
                    <div class="accept-order"
                        v-if="searchResult.status === 'waiting_shipment'"
                        @click="handleShip($event, searchResult)"
                        :disabled="loadingActions[searchResult.id] === 'ship'"
                    >
                        {{ loadingActions[searchResult.id] === 'ship' ? 'Обработка...' : 'Заказ отправлен' }}
                    </div>
                    
                    <div 
                        class="canseled-order" 
                        v-if="['waiting_shipment', 'waiting_confirmation', 'waiting_order', 'ordered', 'in_china'].includes(searchResult.status)"
                        @click="handleCancel($event, searchResult)"
                        :disabled="loadingActions[searchResult.id] === 'cancel'"
                    >
                        {{ loadingActions[searchResult.id] === 'cancel' ? 'Обработка...' : 'Отменить' }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="sizeModalVisible" class="size-modal-overlay" @click.self="closeSizeModal">
            <div class="size-modal-card">
                <div class="size-modal-header">
                    <div class="size-modal-title">Изменить размер</div>
                    <button class="size-modal-close" @click="closeSizeModal" :disabled="sizeModalSubmitting">✕</button>
                </div>

                <div class="size-modal-order">
                    <div>Заказ: <strong>№{{ sizeModalOrder && sizeModalOrder.order_id }}</strong></div>
                    <div>Текущий размер: <strong>{{ sizeModalCurrentSize }}</strong></div>
                    <div>Количество: <strong>{{ sizeModalQuantity }} шт.</strong></div>
                </div>

                <div class="size-modal-options">
                    <button
                        v-for="option in sizeModalAvailableSizes"
                        :key="option.size_name"
                        class="size-option-btn"
                        :class="{ 'size-option-selected': sizeModalSelectedSize === option.size_name }"
                        @click="sizeModalSelectedSize = option.size_name"
                        :disabled="sizeModalSubmitting"
                    >
                        <span>{{ option.size_name }}</span>
                        <span>{{ option.quantity }} шт.</span>
                    </button>
                </div>

                <div class="size-modal-actions">
                    <button class="size-modal-cancel" @click="closeSizeModal" :disabled="sizeModalSubmitting">
                        Отмена
                    </button>
                    <button
                        class="size-modal-confirm"
                        @click="confirmSizeChange"
                        :disabled="!sizeModalSelectedSize || sizeModalSubmitting"
                    >
                        {{ sizeModalSubmitting ? 'Сохранение...' : 'Подтвердить' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api/api';

export default {
    name: 'SearchOrderComponent',
    props: {
        // Поиск доступен только менеджерам, поэтому действия идут через /manager/*
        admin_orders: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            searchQuery: '',
            loading: false,
            error: null,
            noResults: false,
            searchResult: null,
            loadingActions: {}, // Для отслеживания действий с заказом
            abroadSteps: [
                { from: 'waiting_order', action: 'order', label: 'Заказан', newStatus: 'ordered', title: 'Заказан' },
                { from: 'ordered', action: 'arrived-china', label: 'Прибыл в Китай', newStatus: 'in_china', title: 'Прибыл в Китай' },
                { from: 'in_china', action: 'send-to-russia', label: 'Отправлено в РФ', newStatus: 'transit_to_rf', title: 'Отправлено в РФ' },
                { from: 'transit_to_rf', action: 'arrived-russia', label: 'Прибыл в РФ', newStatus: 'in_rf', title: 'Прибыл в РФ' }
            ],
            sizeModalVisible: false,
            sizeModalOrder: null,
            sizeModalAvailableSizes: [],
            sizeModalCurrentSize: '',
            sizeModalQuantity: 0,
            sizeModalSelectedSize: '',
            sizeModalSubmitting: false
        };
    },
    computed: {
        managerTgId() {
            const tgId = localStorage.getItem('tg_id');
            return tgId ? Number(tgId) : null;
        }
    },
    methods: {
        canChangeSize(order) {
            return ['waiting_confirmation', 'waiting_shipment'].includes(order.status);
        },

        async getAvailableSizes(order) {
            const response = await api.get(`/manager/orders/${order.order_id}/available-sizes`, {
                params: {
                    manager_tg_id: this.managerTgId
                }
            });

            return response.data;
        },

        openSizeModal(order, availableData) {
            this.sizeModalOrder = order;
            this.sizeModalAvailableSizes = availableData.available_sizes || [];
            this.sizeModalCurrentSize = availableData.current_size || '';
            this.sizeModalQuantity = availableData.quantity || 0;
            this.sizeModalSelectedSize = this.sizeModalAvailableSizes.length
                ? this.sizeModalAvailableSizes[0].size_name
                : '';
            this.sizeModalVisible = true;
        },

        resetSizeModalState() {
            this.sizeModalVisible = false;
            this.sizeModalOrder = null;
            this.sizeModalAvailableSizes = [];
            this.sizeModalCurrentSize = '';
            this.sizeModalQuantity = 0;
            this.sizeModalSelectedSize = '';
            this.sizeModalSubmitting = false;
        },

        closeSizeModal() {
            if (this.sizeModalSubmitting) {
                return;
            }
            this.resetSizeModalState();
        },

        async handleSizeChange(event, order) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                return;
            }

            if (!this.managerTgId) {
                this.showNotification('error', 'Ошибка', 'Не найден Telegram ID менеджера');
                return;
            }

            try {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: 'change_size'
                };

                const availableData = await this.getAvailableSizes(order);
                if (!availableData.available_sizes || availableData.available_sizes.length === 0) {
                    this.showNotification('warning', 'Нет размеров', 'Нет доступных размеров для замены');
                    return;
                }
                this.openSizeModal(order, availableData);
            } catch (error) {
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось изменить размер';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
            }
        },

        async confirmSizeChange() {
            if (!this.sizeModalOrder || !this.sizeModalSelectedSize) {
                return;
            }

            if (!this.managerTgId) {
                this.showNotification('error', 'Ошибка', 'Не найден Telegram ID менеджера');
                return;
            }

            try {
                this.sizeModalSubmitting = true;

                const response = await api.post(`/manager/orders/${this.sizeModalOrder.order_id}/change-size`, {
                    new_size: this.sizeModalSelectedSize,
                    manager_tg_id: this.managerTgId
                });

                if (!response.data.success) {
                    throw new Error(response.data.message || 'Не удалось изменить размер');
                }

                this.sizeModalOrder.size = response.data.new_size;

                let successMessage = response.data.message;
                if (response.data.is_drop && response.data.notification_sent) {
                    successMessage += ' Уведомление дроп-пользователю отправлено.';
                }

                this.showNotification('success', 'Размер изменен', successMessage);
                this.$emit('order-updated', this.sizeModalOrder);
                this.resetSizeModalState();
            } catch (error) {
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось изменить размер';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                this.sizeModalSubmitting = false;
            }
        },

        // Поиск заказа через новый endpoint
        async performSearch() {
            const query = this.searchQuery.trim();
            if (!query) {
                this.error = 'Введите номер заказа для поиска';
                return;
            }

            this.loading = true;
            this.error = null;
            this.noResults = false;
            this.searchResult = null;

            try {
                // Используем новый endpoint для поиска
                const response = await api.get('/manager/orders/search', {
                    params: {
                        order_number: query
                    }
                });
                
                console.log('Ответ от сервера:', response.data);

                if (response.data.success && response.data.found) {
                    this.searchResult = response.data.order;
                    this.noResults = false;
                    this.error = null;
                    this.$emit('order-found', response.data.order);
                } else {
                    this.noResults = true;
                    this.searchResult = null;
                    this.error = response.data.message || 'Заказ не найден';
                }

            } catch (error) {
                console.error('Ошибка поиска заказа:', error);
                
                // Если сервер вернул 404 (заказ не найден)
                if (error.response && error.response.status === 404) {
                    this.noResults = true;
                    this.error = `Заказ с номером "${query}" не найден`;
                    this.searchResult = null;
                } else {
                    this.error = 'Не удалось выполнить поиск. Проверьте подключение.';
                    this.noResults = false;
                    this.searchResult = null;
                }
            } finally {
                this.loading = false;
            }
        },

        // Очистка поиска
        clearSearch() {
            this.searchQuery = '';
            this.searchResult = null;
            this.error = null;
            this.noResults = false;
            this.$emit('clear-search');
        },

        // Методы для действий с заказом
        async handleConfirm(event, order) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                console.log('Заказ уже обрабатывается:', order.order_id);
                return;
            }
            
            console.log('Начало подтверждения заказа:', order.order_id);
            
            try {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: 'confirm'
                };
                
                // Подтверждаем действие
                const confirmed = confirm(`Подтвердить заказ №${order.order_id}?\nТовар: ${order.product_name}\nРазмер: ${order.size} x ${order.quantity}`);
                
                if (!confirmed) {
                    this.loadingActions = {
                        ...this.loadingActions,
                        [order.order_id]: false
                    };
                    console.log('Пользователь отменил подтверждение заказа');
                    return;
                }

                console.log('Отправка запроса на подтверждение заказа:', order.order_id);
                
                // Используем order.order_id вместо order.id
                const response = await api.post(`/manager/orders/${order.order_id}/confirm`);
                
                console.log('Ответ от сервера (подтверждение):', response.data);

                if (response.data.success) {
                    // Обновляем статус заказа локально
                    order.status = 'waiting_shipment';
                    
                    // Показываем уведомление об успехе
                    let successMessage = response.data.message;
                    
                    // Добавляем информацию о списании со склада
                    if (response.data.stock_updated) {
                        successMessage += ` Товар "${response.data.product_name}" (${response.data.size}) x ${response.data.stock_deducted} шт. списан со склада.`;
                    }
                    
                    // Добавляем информацию об уведомлении если это дроп-заказ
                    if (response.data.is_drop && response.data.notification_sent) {
                        const username = response.data.drop_username || 'пользователь';
                        successMessage += ` Уведомление отправлено ${username}.`;
                    }
                    
                    this.showNotification('success', 'Заказ подтвержден', successMessage);
                    
                    // Эмитим событие для обновления родительского компонента
                    this.$emit('order-updated', order);
                    
                    console.log('Заказ успешно подтвержден:', order.order_id);
                } else {
                    throw new Error(response.data.message || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Ошибка при подтверждении заказа:', error);
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось подтвердить заказ';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                // Сбрасываем состояние загрузки
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
                console.log('Завершение обработки подтверждения заказа:', order.order_id);
            }
        },

        async handleShip(event, order) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                console.log('Заказ уже обрабатывается:', order.order_id);
                return;
            }
            
            console.log('Отправка заказа:', order.order_id);
            
            // Проверяем, можно ли отправить заказ
            if (!this.canShipOrder(order)) {
                this.showNotification('warning', 'Внимание', 'Заказ нельзя отправить в текущем статусе');
                return;
            }

            await this.confirmShipOrderDirect(order);
        },

        canShipOrder(order) {
            // Теперь отправка возможна только из статуса waiting_shipment
            const allowedStatuses = ['waiting_shipment'];
            const allowedTypes = ['drop', 'avito'];
            
            return allowedStatuses.includes(order.status) && 
                   allowedTypes.includes(order.order_type);
        },

        async handleAdminReturn(event, order) {
            console.log('Админский возврат заказа:', order.order_id);
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            // Проверяем, что это заказ с Авито
            if (order.order_type !== 'avito') {
                this.showNotification('warning', 'Внимание', 'Возврат через эту кнопку доступен только для заказов с Авито');
                return;
            }

            if (this.loadingActions[order.order_id]) {
                console.log('Заказ уже обрабатывается:', order.order_id);
                return;
            }
            
            console.log('Начало возврата заказа Авито для администратора:', order.order_id);
            
            try {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: 'return'
                };
                
                console.log('Отправка запроса на возврат заказа Авито:', order.order_id);
                
                // Используем order.order_id вместо order.id
                const response = await api.post(`/manager/orders/${order.order_id}/return`);
                
                console.log('Ответ от сервера:', response.data);

                if (response.data.success) {
                    // Обновляем статус заказа локально
                    order.status = 'on_return';
                    
                    // Показываем уведомление об успехе
                    let successMessage = response.data.message;
                    if (response.data.product_name && response.data.size && response.data.quantity_returned) {
                        successMessage += ` Товар "${response.data.product_name}" (${response.data.size}) x ${response.data.quantity_returned} шт. возвращен в наличие.`;
                    }
                    
                    this.showNotification('success', 'Возврат оформлен', successMessage);
                    
                    // Эмитим событие для обновления родительского компонента
                    this.$emit('order-updated', order);
                    
                    console.log('Возврат заказа Авито успешно оформлен:', order.order_id);
                } else {
                    throw new Error(response.data.message || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Ошибка при оформлении возврата заказа Авито:', error);
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось оформить возврат';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                // Сбрасываем состояние загрузки
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
                console.log('Завершение обработки возврата заказа:', order.order_id);
            }
        },

        // === Закрытие возврата (товар физически получен обратно) ===
        async handleCloseReturn(event, order) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                return;
            }

            try {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: 'close_return'
                };

                const response = await api.post(`/manager/orders/${order.order_id}/close-return`, null, {
                    params: {
                        manager_tg_id: this.managerTgId
                    }
                });

                if (response.data.success) {
                    order.status = 'return_closed';

                    let successMessage = response.data.message;
                    if (response.data.product_name && response.data.size && response.data.quantity_returned) {
                        successMessage += ` Товар "${response.data.product_name}" (${response.data.size}) x ${response.data.quantity_returned} шт. возвращен в наличие.`;
                    }

                    this.showNotification('success', 'Возврат закрыт', successMessage);
                    this.$emit('order-updated', order);
                } else {
                    throw new Error(response.data.message || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Ошибка при закрытии возврата:', error);
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось закрыть возврат';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
            }
        },

        // === Шаги заграничного конвейера (как в списке заказов) ===
        async handleAbroadStep(event, order, step) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                return;
            }

            const confirmed = confirm(
                `Перевести заказ №${order.order_id} в статус "${step.title}"?\n\n` +
                `Товар: ${order.product_name}\n` +
                `Размер: ${order.size} x ${order.quantity} шт.`
            );
            if (!confirmed) {
                return;
            }

            try {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: step.action
                };

                const response = await api.post(`/manager/orders/${order.order_id}/${step.action}`);

                if (response.data.success) {
                    order.status = response.data.new_status || step.newStatus;

                    let successMessage = response.data.message || `Заказ №${order.order_id} переведен в статус "${step.title}"`;
                    if (response.data.is_drop) {
                        if (response.data.notification_sent) {
                            successMessage += `\nУведомление отправлено ${response.data.drop_username || 'пользователю'}.`;
                        } else if (response.data.notification_error) {
                            successMessage += `\nНе удалось отправить уведомление: ${response.data.notification_error}`;
                        }
                    }

                    this.showNotification('success', 'Статус обновлен', successMessage);
                    this.$emit('order-updated', order);
                } else {
                    throw new Error(response.data.message || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Ошибка при смене статуса заказа:', error);
                const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Не удалось изменить статус заказа';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
            }
        },

        async handleCancel(event, order) {
            // Предотвращаем всплытие события
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (this.loadingActions[order.order_id]) {
                console.log('Заказ уже обрабатывается:', order.order_id);
                return;
            }
            
            console.log('Начало отмены заказа:', order.order_id);
            
            try {
                // Устанавливаем состояние загрузки для этого заказа
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: 'cancel'
                };
                
                // Спрашиваем причину отмены (необязательно)
                const reason = prompt('Укажите причину отмены (необязательно):');
                if (reason === null) {
                    // Пользователь отменил ввод
                    console.log('Пользователь отменил ввод причины');
                    this.loadingActions = {
                        ...this.loadingActions,
                        [order.order_id]: false
                    };
                    return;
                }

                console.log('Отправка запроса на отмену заказа:', order.order_id);
                
                // РАЗДЕЛЕНИЕ: для админов используем новый endpoint с параметром в пути
                if (this.admin_orders) {
                    // НОВЫЙ endpoint для администраторов - используем order.order_id
                    const data = {
                        reason: reason || ''
                    };
                    
                    console.log('Используем новый endpoint для админов:', data);
                    const response = await api.post(`/manager/orders/${order.order_id}/cancel`, data);
                    
                    console.log('Ответ от сервера (админ):', response.data);
                    
                    if (response.data.success) {
                        // Обновляем статус заказа локально
                        order.status = 'canceled';
                        
                        // Показываем уведомление об успехе
                        let successMessage = response.data.message;
                        
                        // Добавляем информацию о возврате в наличие если есть
                        if (response.data.stock_returned) {
                            successMessage += ` Товар "${response.data.product_name}" (${response.data.size}) x ${response.data.quantity_returned} шт. возвращен в наличие.`;
                        }
                        
                        // Добавляем информацию об уведомлении если это дроп-заказ
                        if (response.data.is_drop && response.data.notification_sent) {
                            const username = response.data.drop_username || 'пользователь';
                            successMessage += ` Уведомление отправлено ${username}.`;
                        }
                        
                        this.showNotification('success', 'Заказ отменен', successMessage);
                        
                        // Эмитим событие для обновления родительского компонента
                        this.$emit('order-updated', order);
                        
                        console.log('Заказ успешно отменен (админ):', order.order_id);
                    } else {
                        throw new Error(response.data.message || 'Неизвестная ошибка');
                    }
                } else {
                    // Старый endpoint для дроп-пользователей
                    const endpoint = '/drop/profile/orders/cancel';
                    
                    const data = {
                        order_id: order.order_id, // Используем order.order_id
                        reason: reason || 'Причина не указана'
                    };
                    
                    // Добавляем profile_id только если не админский режим
                    if (!this.admin_orders) {
                        data.profile_id = parseInt(this.profileId);
                    }

                    const response = await api.post(endpoint, data);

                    console.log('Ответ от сервера (дроп):', response.data);

                    if (response.data.status === 'success') {
                        // Обновляем статус заказа локально
                        order.status = 'canceled';
                        
                        // Показываем уведомление об успехе
                        this.showNotification('success', 'Заказ отменен', response.data.message);
                        
                        // Эмитим событие для обновления родительского компонента
                        this.$emit('order-updated', order);
                        
                        console.log('Заказ успешно отменен (дроп):', order.order_id);
                    } else {
                        throw new Error(response.data.message || 'Неизвестная ошибка');
                    }
                }
            } catch (error) {
                console.error('Ошибка при отмене заказа:', error);
                const errorMessage = error.response?.data?.detail || error.message || 'Не удалось отменить заказ';
                this.showNotification('error', 'Ошибка', errorMessage);
            } finally {
                // Сбрасываем состояние загрузки
                this.loadingActions = {
                    ...this.loadingActions,
                    [order.order_id]: false
                };
                console.log('Завершение обработки отмены заказа:', order.order_id);
            }
        },

        // Вспомогательные методы
        getStatusText(status) {
            const statusMap = {
                'waiting_confirmation': 'Ожидает подтверждения',
                'waiting_shipment': 'Ожидает отправки',
                'in_transit': 'В пути',
                'delivered_in_pvz': 'Доставлен в ПВЗ',
                'payout_wait': 'Ожидает выплаты',
                'on_return': 'Возврат',
                'return_closed': 'Закрыт после возврата',
                'disputed': 'Спор',
                'finished': 'Завершен',
                'canceled': 'Отменен'
            };
            return statusMap[status] || status;
        },

        showNotification(type, title, message) {
            try {
                if (this.$notify) {
                    this.$notify({
                        title: title,
                        message: message,
                        type: type,
                        duration: 5000
                    });
                } else if (this.$message) {
                    this.$message({
                        message: message,
                        type: type,
                        duration: 5000
                    });
                } else {
                    alert(`${title}: ${message}`);
                }
            } catch (error) {
                alert(`${title}: ${message}`);
            }
        }
    }
};
</script>

<style scoped>
/* Стили такие же как в предыдущем компоненте, только для одного результата */
.search-order-component {
    width: 100%;
    margin-bottom: 3vw;
}

.search-container {
    background-color: rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 2vw;
    margin-bottom: 1.5vw;
}

.search-input-wrapper {
    display: flex;
    gap: 1vw;
    align-items: center;
    margin-bottom: 1vw;
}

.search-input {
    flex: 1;
    height: 8vw;
    padding: 0 2vw;
    border: 1px solid #005ADA;
    border-radius: 10px;
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 3.5vw;
    outline: none;
}

.search-input:focus {
    border-color: #0BDA51;
    box-shadow: 0 0 0 2px rgba(11, 218, 81, 0.2);
}

.search-input::placeholder {
    color: rgba(255, 255, 255, 0.5);
    font-size: 3.5vw;
}

.search-button {
    height: 8vw;
    padding: 0 4vw;
    background-color: #005ADA;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 3.5vw;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
}

.search-button:hover:not(:disabled) {
    background-color: #0047b3;
    transform: translateY(-2px);
}

.search-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.clear-button {
    height: 8vw;
    width: 8vw;
    background-color: #F71B47;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 4vw;
    cursor: pointer;
    transition: all 0.3s ease;
}

.clear-button:hover {
    background-color: #e50a33;
    transform: translateY(-2px);
}

.search-status {
    text-align: center;
    padding: 1vw 0;
    font-size: 3.5vw;
}

.search-loading {
    color: #005ADA;
}

.search-error {
    color: #F71B47;
}

.search-no-results {
    color: #F2912E;
}

/* Стили для результата поиска */
.search-result {
    background-color: rgba(0, 90, 218, 0.1);
    border-radius: 10px;
    padding: 2vw;
    margin-top: 1vw;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2vw;
    padding-bottom: 1vw;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.result-header h3 {
    color: #005ADA;
    font-size: 4vw;
    margin: 0;
    font-weight: 500;
}

.result-found {
    color: #0BDA51;
    font-size: 3vw;
    font-weight: 500;
}

/* Карточка найденного заказа */
.found-order-block {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 1.5vw;
    background-color: rgba(255, 255, 255, 0.08);
    height: 45vw;
    border-radius: 10px;
    margin-top: 1.5vw;
    padding: 2vw;
    position: relative;
    border: 2px solid #0BDA51;
}

.found-order-block .drop-order-product-img {
    width: 100%;
    height: 41vw;
    border-radius: 10px;
    object-fit: cover;
}

.found-order-block .drop-orders-data {
    display: flex;
    flex-direction: column;
    color: white;
    font-size: 10px;
}

.found-order-block .drop-orders-data-line {
    display: flex;
    gap: 1vw;
    margin-bottom: 0.2vw;
}

.found-order-block .drop-info-line {
    background-color: rgba(0, 90, 218, 0.1);
    padding: 0.5vw;
    border-radius: 5px;
    margin-bottom: 1vw;
}

.found-order-block .drop-order-data-label {
    color: #005ADA;
    min-width: 10vw;
    font-weight: 500;
}

.found-order-block .drop-order-data-value {
    flex: 1;
    word-break: break-word;
}

.found-order-block .order-number-highlight {
    color: #0BDA51;
    font-weight: bold;
    font-size: 11px;
}

.found-order-block .drop-username {
    color: #0BDA51;
    font-size: 0.9em;
}

.found-order-block .payment-paid {
    color: #0BDA51;
    font-weight: bold;
}

.found-order-block .payment-not-paid {
    color: #F71B47;
    font-weight: bold;
}

/* Блок кнопок */
.found-order-block .buttons-block {
    display: flex;
    gap: 1vw;
    position: absolute;
    right: 0;
    bottom: 0;
}

.found-order-block .buttons-block div {
    border-radius: 10px;
    width: 27vw;
    height: 8vw;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 3.5vw;
    font-weight: 500;
    user-select: none;
}

.found-order-block .buttons-block div:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.found-order-block .buttons-block div:active:not(:disabled) {
    transform: translateY(0);
}

.found-order-block .buttons-block div:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.found-order-block .return-order {
    background-color: #F2912E;
}

.found-order-block .return-order:hover:not(:disabled) {
    background-color: #e5821e;
}

.found-order-block .canseled-order {
    background-color: #F71B47;
}

.found-order-block .canseled-order:hover:not(:disabled) {
    background-color: #e50a33;
}

.found-order-block .accept-order {
    background-color: #0BDA51;
}

.found-order-block .size-change-floating {
    position: absolute;
    right: 2vw;
    top: 2vw;
    width: 8vw;
    height: 8vw;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.24);
    color: white;
    font-size: 4vw;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 4;
}

.found-order-block .size-change-floating:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
}

.size-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3000;
    padding: 4vw;
}

.size-modal-card {
    width: 100%;
    max-width: 560px;
    border-radius: 18px;
    background: #1d1f23;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
    color: white;
    padding: 18px;
}

.size-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.size-modal-title {
    font-size: 18px;
    font-weight: 600;
}

.size-modal-close {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: white;
}

.size-modal-order {
    display: grid;
    gap: 6px;
    padding: 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    margin-bottom: 12px;
    font-size: 13px;
}

.size-modal-options {
    display: grid;
    gap: 8px;
    max-height: 42vh;
    overflow-y: auto;
}

.size-option-btn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.04);
    color: white;
    padding: 12px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
}

.size-option-selected {
    border-color: #2AABEE;
    background: rgba(42, 171, 238, 0.2);
}

.size-modal-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 14px;
}

.size-modal-cancel,
.size-modal-confirm {
    height: 42px;
    border-radius: 12px;
    border: none;
    color: white;
    font-size: 14px;
    font-weight: 600;
}

.size-modal-cancel {
    background: rgba(255, 255, 255, 0.16);
}

.size-modal-confirm {
    background: #0BDA51;
}
</style>
